import { useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import {
  ExpandMoreRounded,
  FactoryRounded,
  Inventory2Rounded,
  LocalShippingRounded,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import type {
  MaterialStockProjectionRow,
  MaterialStockPurchaseEvent,
  MaterialStockRequirementEvent,
} from '../types/industrialCost';
import { getMaterialDetail } from '../services/industrialCostService';
import { closingBalanceToday, coverageState } from '../utils/coverageState';
import type { CoverageStateKey } from '../utils/coverageState';
import { buildMaterialForecast, coverageWorkdays } from '../utils/materialForecast';

import { esNumberFormat } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import type { SortValue } from '../utils/tableSort';
import SortableHeaderCell, { TEN_ROWS_MAX_HEIGHT } from '../components/SortableHeaderCell';
import IndustrialCostStockExplanationBlock from './IndustrialCostStockExplanationBlock';
import {
  abcLabel,
  adjustedStock,
  ConsumersSection,
  ConsumptionTab,
  CountsTab,
  DetailError,
  DetailLoading,
  EntriesSection,
  ForecastSection,
  HighlightsSection,
  HistoryNotes,
  LoadingValue,
  rotationValue,
} from './IndustrialCostMaterialDetailSections';
import { buildMaterialHighlights } from '../utils/materialHighlights';
import { realMaterialName } from '../utils/plantPanel';

// IND-024: pestañas del detalle; IND-025 añade Recuentos.
type DetailTab = 'resumen' | 'consumo' | 'entradas' | 'of' | 'recuentos' | 'datos';

function materialKindLabel(material: MaterialStockProjectionRow): string | null {
  if (material.packaging_type === 'CAJA') return 'Caja real';
  if (material.packaging_type === 'O') return 'Embalaje';
  const type = String(material.material_type ?? '').trim();
  return type ? `Tipo ${type}` : null;
}

// IND-026: columnas ordenables; por defecto, por fecha (lo primero que llega).
type RequirementSortKey = 'dispatch' | 'order' | 'ref' | 'machine' | 'qty';
const REQUIREMENT_COLUMNS: Array<{
  key: RequirementSortKey;
  label: string;
  value: (event: MaterialStockRequirementEvent) => SortValue;
}> = [
  { key: 'dispatch', label: 'Expedición', value: (event) => event.dispatch_date?.slice(0, 10) },
  { key: 'order', label: 'OF', value: (event) => event.manufacturing_order_number },
  { key: 'ref', label: 'Referencia', value: (event) => event.product_ref },
  { key: 'machine', label: 'Máquina', value: (event) => event.machine },
  { key: 'qty', label: 'Cantidad', value: (event) => event.material_quantity },
];

type PurchaseSortKey = 'delivery' | 'order' | 'supplier' | 'qty';
const PURCHASE_COLUMNS: Array<{
  key: PurchaseSortKey;
  label: string;
  value: (event: MaterialStockPurchaseEvent) => SortValue;
}> = [
  { key: 'delivery', label: 'Entrega prevista', value: (event) => event.expected_delivery_date?.slice(0, 10) },
  { key: 'order', label: 'Pedido', value: (event) => event.purchase_order_number },
  { key: 'supplier', label: 'Proveedor', value: (event) => event.supplier_name },
  { key: 'qty', label: 'Cantidad', value: (event) => event.quantity_pending },
];

// IND-024: tablas de OF y de pedidos de compra (pestañas y día seleccionado).
// IND-026: 10 filas visibles y scroll interno.
function RequirementsTable({
  events,
  emptyText,
}: {
  events: MaterialStockRequirementEvent[];
  emptyText: string;
}) {
  const sort = useTableSort<RequirementSortKey>('dispatch', 'asc');
  const sortColumn = REQUIREMENT_COLUMNS.find((column) => column.key === sort.key) ?? REQUIREMENT_COLUMNS[0];
  const rows = sortRows(events, sortColumn.value, sort.direction);
  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, maxHeight: TEN_ROWS_MAX_HEIGHT }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {REQUIREMENT_COLUMNS.map((column) => (
              <SortableHeaderCell
                key={column.key}
                label={column.label}
                columnKey={column.key}
                sort={sort}
                align={column.key === 'qty' ? 'right' : 'left'}
                firstDirection={column.key === 'qty' ? 'desc' : 'asc'}
              />
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((event, index) => (
            <TableRow
              key={`${event.manufacturing_order_number}-${event.material_quantity}-${index}`}
            >
              <TableCell>
                {fmtDate(event.dispatch_date)}
              </TableCell>
              <TableCell>{event.manufacturing_order_number || '—'}</TableCell>
              <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                {event.product_ref || '—'}
              </TableCell>
              <TableCell>{event.machine || '—'}</TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 900 }}
              >
                {fmt(event.material_quantity)}
              </TableCell>
            </TableRow>
          ))}

          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 12,
                    textAlign: 'center',
                    py: 2,
                  }}
                >
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function PurchasesTable({
  events,
  emptyText,
  emptyIsWarning,
  shortageDate,
}: {
  events: MaterialStockPurchaseEvent[];
  emptyText: string;
  emptyIsWarning: boolean;
  shortageDate?: string | null;
}) {
  const sort = useTableSort<PurchaseSortKey>('delivery', 'asc');
  const sortColumn = PURCHASE_COLUMNS.find((column) => column.key === sort.key) ?? PURCHASE_COLUMNS[0];
  const rows = sortRows(events, sortColumn.value, sort.direction);
  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, maxHeight: TEN_ROWS_MAX_HEIGHT }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {PURCHASE_COLUMNS.map((column) => (
              <SortableHeaderCell
                key={column.key}
                label={column.label}
                columnKey={column.key}
                sort={sort}
                align={column.key === 'qty' ? 'right' : 'left'}
                firstDirection={column.key === 'qty' ? 'desc' : 'asc'}
              />
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((event, index) => {
            // IND-024: pedido con fecha que llega después de la primera rotura.
            const late = Boolean(
              shortageDate &&
                event.expected_delivery_date &&
                event.purchase_status !== 'overdue' &&
                event.expected_delivery_date.slice(0, 10) > shortageDate.slice(0, 10),
            );
            return (
              <TableRow
                key={`${event.purchase_order_number}-${event.quantity_pending}-${index}`}
              >
                <TableCell>
                  <Stack spacing={0.25}>
                    <Typography sx={{ fontSize: 12 }}>
                      {fmtDate(event.expected_delivery_date)}
                    </Typography>
                    {event.is_overdue ? (
                      <Typography
                        sx={{
                          color: '#fbbf24',
                          fontSize: 9.5,
                          fontWeight: 850,
                        }}
                      >
                        Vencida
                      </Typography>
                    ) : null}
                    {late ? (
                      <Typography
                        sx={{
                          color: '#f87171',
                          fontSize: 9.5,
                          fontWeight: 850,
                        }}
                      >
                        Llega después de la rotura
                      </Typography>
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>{event.purchase_order_number || '—'}</TableCell>
                <TableCell sx={{ color: '#f8fafc', fontWeight: 700 }}>
                  {event.supplier_name || '—'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 900 }}
                >
                  {fmt(event.quantity_pending)}
                </TableCell>
              </TableRow>
            );
          })}

          {events.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography
                  sx={{
                    color: emptyIsWarning
                      ? '#f87171'
                      : '#94a3b8',
                    fontSize: 12,
                    textAlign: 'center',
                    py: 2,
                    fontWeight: 850,
                  }}
                >
                  {emptyText}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
type Props = {
  material: MaterialStockProjectionRow | null;
  calculationDate?: string | null;
  onClose: () => void;
};

type DailyCoverage = {
  date: string;
  purchaseQty: number;
  requirementQty: number;
  purchaseCount: number;
  requirementCount: number;
  closingBalance: number | null;
  hasMovement: boolean;
};

type WeeklyCoverageGroup = {
  weekKey: string;
  label: string;
  days: DailyCoverage[];
};

const numberFormat = esNumberFormat({
  maximumFractionDigits: 1,
});

function fmt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }

  return numberFormat.format(value);
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';

  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);

  return Number.isNaN(parsed.getTime())
    ? value
    : parsed.toLocaleDateString('es-ES');
}

// IND-009: mismos colores que la tabla de cobertura.
const STATE_COLOR: Record<CoverageStateKey, string> = {
  rotura: '#f87171',
  confirmar: '#fbbf24',
  tarde: '#fbbf24',
  sin_stock: '#cbd5e1',
  cubierto: '#4ade80',
};

function validUnitLabel(value: string | null | undefined): string | null {
  const normalized = String(value ?? '').trim();

  if (
    !normalized ||
    ['nan', 'none', 'null'].includes(normalized.toLowerCase())
  ) {
    return null;
  }

  return normalized;
}

function toDateKey(dateValue: Date): string {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function startOfWeekKey(date: string): string {
  const parsed = new Date(`${date}T12:00:00`);
  const day = parsed.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  parsed.setDate(parsed.getDate() + diff);

  return toDateKey(parsed);
}

function addDays(date: string, offset: number): string {
  const parsed = new Date(`${date}T12:00:00`);

  parsed.setDate(parsed.getDate() + offset);

  return toDateKey(parsed);
}

function buildWeekLabel(weekKey: string): string {
  return `Semana ${fmtDate(weekKey)} · ${fmtDate(addDays(weekKey, 6))}`;
}

export default function IndustrialCostMaterialCoverageDialog({
  material,
  calculationDate,
  onClose,
}: Props) {
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [tab, setTab] = useState<DetailTab>('resumen');
  const [showDaily, setShowDaily] = useState(false);

  useEffect(() => {
    setSelectedDay(null);
    setTab('resumen');
    setShowDaily(false);
  }, [material?.material_id]);

  // IND-024: el detalle abre con la fila actual; lo nuevo llega después.
  const detailQuery = useQuery({
    // Con el prefijo de la proyección: las cargas que la invalidan, también.
    queryKey: ['industrial-cost-material-stock-projection', 'detail', material?.material_id],
    queryFn: () => getMaterialDetail(material!.material_id),
    enabled: Boolean(material?.material_id),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const detail = detailQuery.data;
  // IND-026: el fallo (o más de 30 s) solo afecta a lo nuevo, con «Reintentar».
  const detailErrorBlock = (
    <DetailError
      error={detailQuery.error}
      onRetry={() => void detailQuery.refetch()}
      retrying={detailQuery.isFetching}
    />
  );

  const forecast = useMemo(() => {
    if (!material || !detail) return null;
    return buildMaterialForecast({
      stock: material.calculated_stock_qty,
      timelineEvents: material.timeline_events,
      today: (calculationDate ?? detail.calculation_date).slice(0, 10),
      perWorkday: detail.rhythm.per_workday,
      holidays: detail.upcoming_holidays,
    });
  }, [material, detail, calculationDate]);

  // IND-025: «Lo importante» (máximo 4 frases).
  const highlights = useMemo(
    () =>
      material && detail
        ? buildMaterialHighlights({
            material,
            detail,
            forecast,
            unit: validUnitLabel(material.unit_label) ?? '',
          })
        : [],
    [material, detail, forecast],
  );

  const dailyCoverage = useMemo<DailyCoverage[]>(() => {
    if (!material) return [];

    const grouped = new Map<string, DailyCoverage>();

    for (const event of material.timeline_events) {
      const date = event.effective_date.slice(0, 10);

      const current = grouped.get(date) ?? {
        date,
        purchaseQty: 0,
        requirementQty: 0,
        purchaseCount: 0,
        requirementCount: 0,
        closingBalance: event.balance_after_qty,
        hasMovement: true,
      };

      if (event.event_type === 'purchase') {
        current.purchaseQty += Math.max(event.quantity_delta, 0);
        current.purchaseCount += 1;
      } else {
        current.requirementQty += Math.abs(
          Math.min(event.quantity_delta, 0),
        );
        current.requirementCount += 1;
      }

      current.closingBalance = event.balance_after_qty;
      grouped.set(date, current);
    }

    return [...grouped.values()].sort((a, b) =>
      a.date.localeCompare(b.date),
    );
  }, [material]);

  const weeklyCoverage = useMemo<WeeklyCoverageGroup[]>(() => {
    const coverageByDate = new Map(
      dailyCoverage.map((day) => [day.date, day]),
    );

    const sortedKeys = [
      ...new Set(
        dailyCoverage.map((day) => startOfWeekKey(day.date)),
      ),
    ].sort((a, b) => a.localeCompare(b));

    // IND-009: semanas seguidas (sin huecos) y el saldo de cada día, también
    // de los días sin movimientos.
    const weekKeys: string[] = [];
    if (sortedKeys.length > 0) {
      for (
        let key = sortedKeys[0];
        key <= sortedKeys[sortedKeys.length - 1] && weekKeys.length < 60;
        key = addDays(key, 7)
      ) {
        weekKeys.push(key);
      }
    }
    let carried: number | null = material?.calculated_stock_qty ?? null;

    return weekKeys.map((weekKey) => ({
      weekKey,
      label: buildWeekLabel(weekKey),
      // IND-009: semanas completas, también sábados y domingos.
      days: Array.from({ length: 7 }, (_, index) => {
        const date = addDays(weekKey, index);
        const day = coverageByDate.get(date);
        if (day) {
          carried = day.closingBalance;
          return day;
        }
        return {
          date,
          purchaseQty: 0,
          requirementQty: 0,
          purchaseCount: 0,
          requirementCount: 0,
          closingBalance: carried,
          hasMovement: false,
        };
      }),
    }));
  }, [dailyCoverage, material]);

  const visibleRequirements = useMemo(() => {
    if (!material) return [];

    if (!selectedDay) {
      return material.requirement_events;
    }

    return material.requirement_events.filter(
      (event) =>
        event.effective_date?.slice(0, 10) === selectedDay,
    );
  }, [material, selectedDay]);

  const visiblePurchases = useMemo(() => {
    if (!material) return [];

    if (!selectedDay) {
      return material.purchase_events;
    }

    return material.purchase_events.filter(
      (event) =>
        event.effective_date?.slice(0, 10) === selectedDay,
    );
  }, [material, selectedDay]);

  if (!material) return null;

  const state = coverageState(material, calculationDate);
  const color = STATE_COLOR[state.key];
  const unitLabel = validUnitLabel(material.unit_label);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: 'min(1680px, calc(100vw - 32px))',
          maxWidth: '1680px',
          height: 'min(900px, calc(100vh - 32px))',
          maxHeight: '900px',
          m: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={1.25}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 950,
              }}
            >
              {/* IND-015: «nan» no es un nombre */}
              {realMaterialName(material.material_name, material.material_code) ||
                material.material_code ||
                'Materia prima'}
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {[
                // IND-029: el código una sola vez si no hay nombre real.
                realMaterialName(material.material_name, material.material_code) ? material.material_code : null,
                materialKindLabel(material),
                unitLabel,
                // IND-024: productos y líneas que lo han consumido (12 meses).
                detail
                  ? `${detail.products_count} ${detail.products_count === 1 ? 'producto' : 'productos'} y ${detail.lines_count} ${detail.lines_count === 1 ? 'línea' : 'líneas'} lo usan (12 meses)`
                  : null,
                // IND-025: clase ABC dentro de su grupo de unidad.
                detail ? abcLabel(detail.abc) : null,
              ].filter(Boolean).join(' · ')}
            </Typography>
          </Box>

          <Chip
            label={state.detail ? `${state.label} · ${state.detail}` : state.label}
            sx={{
              alignSelf: { xs: 'flex-start', md: 'center' },
              color,
              bgcolor: `${color}14`,
              border: `1px solid ${color}55`,
              fontWeight: 900,
            }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          overflow: 'hidden',
          pb: 1.5,
        }}
      >
        {/* IND-024: pestañas del detalle */}
        <Tabs
          value={tab}
          onChange={(_, value: DetailTab) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ minHeight: 36, flexShrink: 0, '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 850 } }}
        >
          <Tab value="resumen" label="Resumen" />
          <Tab value="consumo" label="Consumo" />
          <Tab value="entradas" label="Entradas y compras" />
          <Tab value="of" label={`OF (${material.requirement_events.length})`} />
          <Tab value="recuentos" label="Recuentos" />
          <Tab value="datos" label="Datos" />
        </Tabs>

        {/* IND-026: cada pestaña hace scroll dentro de la ventana; los bloques
            no se encogen (ninguna tabla queda recortada). */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pr: 0.35,
            '& > *': { flexShrink: 0 },
          }}
        >
        {tab === 'resumen' ? (
        <>
        {/* IND-025 */}
        <HighlightsSection items={highlights} />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
              lg: 'repeat(7, minmax(0, 1fr))',
            },
            gap: 1,
            flexShrink: 0,
          }}
        >
          {[
            {
              label: 'Stock hoy',
              value: fmt(material.calculated_stock_qty),
              // IND-025: con recuentos comparados, el stock ajustado.
              sub: detail?.counts.reliability
                ? `ajustado ${fmt(adjustedStock(material.calculated_stock_qty, detail.counts.reliability))} · fiabilidad ${detail.counts.reliability.level}`
                : undefined,
            },
            {
              // IND-024: stock ÷ consumo medio por día laborable (ritmo habitual).
              label: 'Días de cobertura',
              value: (() => {
                if (!detail) return detailQuery.isError ? '—' : <LoadingValue />;
                const days = coverageWorkdays(material.calculated_stock_qty, detail.rhythm.per_workday);
                return days === null ? '—' : `${fmt(days)} días`;
              })(),
              sub: detail
                ? detail.rhythm.per_workday
                  ? `laborables al ritmo de ${fmt(detail.rhythm.per_workday)}${unitLabel ? ` ${unitLabel}` : ''}/día`
                  : material.calculated_stock_qty === null
                    ? 'sin stock calculado'
                    : `sin consumo en ${detail.rhythm_weeks} semanas`
                : undefined,
            },
            {
              // IND-025: últimos 3 meses (detalle en la pestaña Consumo).
              label: 'Rotación',
              value: (() => {
                if (!detail) return detailQuery.isError ? '—' : <LoadingValue />;
                return rotationValue(detail.rotation.recent).value;
              })(),
              sub: detail
                ? [
                    rotationValue(detail.rotation.recent).note,
                    // IND-028: con periodo recortado ya dice «desde dd/mm».
                    detail.rotation.recent.partial ? null : 'últimos 3 meses',
                  ].filter(Boolean).join(' · ')
                : undefined,
            },
            {
              label: 'Necesidad OF',
              value: fmt(material.pending_requirement_qty),
              sub: `${new Set(material.requirement_events.map((e) => e.manufacturing_order_number)).size} OF`,
            },
            {
              label: 'Compra pendiente',
              value: fmt(material.incoming_pending_qty),
              sub:
                material.purchase_events.length > 0
                  ? (() => {
                      const orders = new Set(material.purchase_events.map((e) => e.purchase_order_number)).size;
                      return `${orders} ${orders === 1 ? 'pedido' : 'pedidos'}`;
                    })() +
                    (material.incoming_overdue_qty + material.incoming_undated_qty > 0
                      ? ' · vencida o sin fecha · confirmar'
                      : material.next_delivery_date
                        ? ` · llega ${fmtDate(material.next_delivery_date)}`
                        : '')
                  : 'sin compras',
              valueColor:
                material.incoming_overdue_qty + material.incoming_undated_qty > 0
                  ? '#fbbf24'
                  : '#f8fafc',
            },
            {
              label: 'Saldo final',
              value: fmt(material.eventual_balance_qty),
              sub: 'tras todas las OF y compras',
              valueColor:
                material.eventual_balance_qty !== null &&
                material.eventual_balance_qty < 0
                  ? '#f87171'
                  : '#f8fafc',
            },
            {
              label: 'Primera rotura',
              value:
                material.calculated_stock_qty === null
                  ? '—'
                  : material.first_shortage_date
                    ? calculationDate &&
                      material.first_shortage_date.slice(0, 10) <=
                        calculationDate.slice(0, 10)
                      ? 'Rotura hoy'
                      : fmtDate(material.first_shortage_date)
                    : 'Sin rotura',
              sub: [
                material.calculated_stock_qty === null
                  ? 'sin stock calculado'
                  : material.first_shortage_date
                    ? (() => {
                        const closing = closingBalanceToday(material, calculationDate);
                        const isToday =
                          calculationDate &&
                          material.first_shortage_date.slice(0, 10) <= calculationDate.slice(0, 10);
                        const deficit =
                          isToday && closing !== null && closing < 0
                            ? -closing
                            : material.first_shortage_qty;
                        return `faltan ${fmt(deficit)}${unitLabel ? ` ${unitLabel}` : ''} ${isToday ? 'al cierre de hoy' : 'ese día'}`;
                      })()
                    : undefined,
                // IND-024: con el ritmo habitual, si la rotura llega antes.
                forecast?.rhythmShortageDate &&
                (!material.first_shortage_date ||
                  forecast.rhythmShortageDate < material.first_shortage_date.slice(0, 10))
                  ? `con el ritmo habitual: ${fmtDate(forecast.rhythmShortageDate)}`
                  : undefined,
              ].filter(Boolean).join(' · ') || undefined,
              // «Sin rotura» en verde solo cuando de verdad no la hay.
              valueColor:
                material.calculated_stock_qty === null
                  ? '#94a3b8'
                  : material.first_shortage_date
                    ? '#f87171'
                    : '#4ade80',
            },
          ].map((item) => (
            <Paper
              key={item.label}
              variant="outlined"
              sx={{
                p: 1.15,
                borderRadius: 2,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 11,
                  fontWeight: 800,
                }}
              >
                {item.label}
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  color: item.valueColor ?? '#f8fafc',
                  fontSize: 18,
                  lineHeight: 1.1,
                  fontWeight: 950,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.value}
              </Typography>
              {'sub' in item && item.sub ? (
                <Typography sx={{ color: 'text.secondary', fontSize: 11, mt: 0.3 }}>
                  {item.sub}
                </Typography>
              ) : null}
            </Paper>
          ))}
        </Box>

        <Box
          sx={{
            px: 1.15,
            py: 0.65,
            borderRadius: 1.5,
            bgcolor: 'rgba(15,23,42,0.28)',
            border: '1px solid rgba(148,163,184,0.10)',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              color: '#94a3b8',
              fontSize: 10.5,
              lineHeight: 1.25,
            }}
          >
            <Box
              component="span"
              sx={{
                color: '#cbd5e1',
                fontWeight: 900,
              }}
            >
              Stock hoy
            </Box>
            {' = '}
            recuento {fmt(material.baseline_count_qty)}
            {material.baseline_count_date
              ? ` (${fmtDate(material.baseline_count_date)})`
              : ''}
            {' + '}
            entradas {fmt(material.entries_since_count_qty)}
            {' − '}
            consumo de producción {fmt(material.technical_consumption_since_count_qty)}
            {' = '}
            <Box
              component="span"
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
              }}
            >
              {fmt(material.calculated_stock_qty)}
            </Box>
          </Typography>
        </Box>

        {(material.incoming_overdue_qty > 0 ||
          material.incoming_undated_qty > 0 ||
          material.undated_requirement_qty > 0) ? (
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
            sx={{ flexShrink: 0 }}
          >
            {material.incoming_overdue_qty > 0 ? (
              <Chip
                size="small"
                label={`${fmt(material.incoming_overdue_qty)} compra vencida · se cuenta hoy · confirmar con proveedor`}
                sx={{
                  color: '#fbbf24',
                  bgcolor: 'rgba(251,191,36,0.10)',
                }}
              />
            ) : null}

            {material.incoming_undated_qty > 0 ? (
              <Chip
                size="small"
                label={`${fmt(material.incoming_undated_qty)} compra sin fecha · se cuenta hoy · confirmar con proveedor`}
                sx={{
                  color: '#fbbf24',
                  bgcolor: 'rgba(251,191,36,0.10)',
                }}
              />
            ) : null}

            {material.undated_requirement_qty > 0 ? (
              <Chip
                size="small"
                label={`${fmt(material.undated_requirement_qty)} necesidad sin fecha · se cuenta hoy`}
                sx={{
                  color: '#fbbf24',
                  bgcolor: 'rgba(251,191,36,0.10)',
                }}
              />
            ) : null}
          </Stack>
        ) : null}

        {/* IND-024: previsión a 8 semanas y quién lo consume */}
        {detail ? (
          <>
            <ForecastSection
              forecast={forecast}
              unit={unitLabel ?? ''}
              hasRhythm={Boolean(detail.rhythm.per_workday)}
              rhythmWeeks={detail.rhythm_weeks}
            />
            <ConsumersSection detail={detail} unit={unitLabel ?? ''} />
          </>
        ) : detailQuery.isError ? (
          detailErrorBlock
        ) : (
          <>
            <DetailLoading height={260} />
            <DetailLoading height={140} />
          </>
        )}

        <Button
          variant="text"
          onClick={() => setShowDaily((current) => !current)}
          endIcon={
            <ExpandMoreRounded
              sx={{ transform: showDaily ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
            />
          }
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 850, flexShrink: 0 }}
        >
          {showDaily ? 'Ocultar el saldo día a día' : 'Ver el saldo día a día'}
        </Button>

        <Collapse in={showDaily} unmountOnExit sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              xl: 'minmax(0, 2.15fr) minmax(360px, 0.85fr)',
            },
            gap: 1.5,
            minHeight: 0,
            height: { xs: 'auto', xl: 620 },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 1.4,
              borderRadius: 2.5,
              overflow: 'hidden',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              mb={1.2}
              sx={{ flexShrink: 0 }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
              >
                <Inventory2Rounded sx={{ color: '#38bdf8' }} />
                <Box>
                  <Typography sx={{ fontWeight: 950 }}>
                    Cobertura por día
                  </Typography>
                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: 10.5,
                    }}
                  >
                    Semanas completas, también sábados y domingos
                  </Typography>
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                {selectedDay ? (
                  <Chip
                    size="small"
                    label={`Día seleccionado · ${fmtDate(selectedDay)}`}
                    sx={{
                      height: 24,
                      fontSize: 10,
                      fontWeight: 850,
                    }}
                  />
                ) : null}

                {selectedDay ? (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelectedDay(null)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 850,
                    }}
                  >
                    Ver todos
                  </Button>
                ) : null}
              </Stack>
            </Stack>

            {weeklyCoverage.length > 0 ? (
              <Box
                sx={{
                  minHeight: 0,
                  flex: 1,
                  overflowY: 'auto',
                  pr: 0.35,
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#334155 transparent',
                  '&::-webkit-scrollbar': {
                    width: 6,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    bgcolor: '#334155',
                    borderRadius: 999,
                  },
                }}
              >
                <Stack spacing={1.15}>
                  {weeklyCoverage.map((week) => (
                    <Box
                      key={week.weekKey}
                      sx={{
                        border: '1px solid rgba(148,163,184,0.10)',
                        borderRadius: 2,
                        p: 1,
                        bgcolor: 'rgba(15,23,42,0.20)',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#cbd5e1',
                          fontSize: 11,
                          fontWeight: 900,
                          mb: 0.8,
                        }}
                      >
                        {week.label}
                      </Typography>

                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(7, minmax(0, 1fr))',
                          },
                          gap: 0.8,
                        }}
                      >
                        {week.days.map((day) => {
                          const selected =
                            day.hasMovement &&
                            selectedDay === day.date;
                          const shortage =
                            day.hasMovement &&
                            day.closingBalance !== null &&
                            day.closingBalance < 0;

                          if (!day.hasMovement) {
                            return (
                              <Box
                                key={day.date}
                                sx={{
                                  minWidth: 0,
                                  minHeight: 114,
                                  px: 1,
                                  py: 0.9,
                                  borderRadius: 2,
                                  border:
                                    '1px dashed rgba(148,163,184,0.12)',
                                  bgcolor: 'rgba(15,23,42,0.12)',
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: '#94a3b8',
                                    fontSize: 10.5,
                                    fontWeight: 900,
                                  }}
                                >
                                  {fmtDate(day.date)}
                                </Typography>

                                <Typography
                                  sx={{
                                    color: '#475569',
                                    fontSize: 9.5,
                                    mt: 3.1,
                                    textAlign: 'center',
                                  }}
                                >
                                  Sin movimientos
                                </Typography>
                                {day.closingBalance !== null ? (
                                  <Typography
                                    sx={{
                                      color: day.closingBalance < 0 ? '#f87171' : '#94a3b8',
                                      fontSize: 11,
                                      fontWeight: 800,
                                      textAlign: 'center',
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {fmt(day.closingBalance)}
                                  </Typography>
                                ) : null}
                              </Box>
                            );
                          }

                          return (
                            <Box
                              key={day.date}
                              component="button"
                              type="button"
                              onClick={() =>
                                setSelectedDay((current) =>
                                  current === day.date
                                    ? null
                                    : day.date,
                                )
                              }
                              sx={{
                                appearance: 'none',
                                textAlign: 'left',
                                color: 'inherit',
                                bgcolor: selected
                                  ? 'rgba(56,189,248,0.10)'
                                  : 'rgba(15,23,42,0.28)',
                                border: selected
                                  ? '1px solid rgba(56,189,248,0.70)'
                                  : shortage
                                    ? '1px solid rgba(248,113,113,0.24)'
                                    : '1px solid rgba(148,163,184,0.10)',
                                borderRadius: 2,
                                px: 1,
                                py: 0.9,
                                cursor: 'pointer',
                                minWidth: 0,
                                minHeight: 114,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                '&:hover': {
                                  bgcolor: 'rgba(56,189,248,0.07)',
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <Typography
                                  sx={{
                                    color: selected
                                      ? '#7dd3fc'
                                      : '#cbd5e1',
                                    fontSize: 10.5,
                                    fontWeight: 950,
                                  }}
                                >
                                  {fmtDate(day.date)}
                                </Typography>

                                {shortage ? (
                                  <Box
                                    sx={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      bgcolor: '#f87171',
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : null}
                              </Stack>

                              <Stack spacing={0.3} mt={0.55}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={0.5}
                                >
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: 9.5,
                                    }}
                                  >
                                    Compras
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: day.purchaseQty > 0
                                        ? '#7dd3fc'
                                        : '#64748b',
                                      fontSize: 12,
                                      fontWeight: 900,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {day.purchaseQty > 0
                                      ? `+${fmt(day.purchaseQty)}`
                                      : '—'}
                                  </Typography>
                                </Stack>

                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  spacing={0.5}
                                >
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: 9.5,
                                    }}
                                  >
                                    Necesidad
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: day.requirementQty > 0
                                        ? '#fca5a5'
                                        : '#64748b',
                                      fontSize: 12,
                                      fontWeight: 900,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {day.requirementQty > 0
                                      ? `−${fmt(day.requirementQty)}`
                                      : '—'}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Box
                                sx={{
                                  mt: 0.7,
                                  pt: 0.65,
                                  borderTop:
                                    '1px solid rgba(148,163,184,0.10)',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="baseline"
                                  spacing={0.5}
                                >
                                  <Typography
                                    sx={{
                                      color: '#94a3b8',
                                      fontSize: 9.5,
                                    }}
                                  >
                                    Saldo
                                  </Typography>

                                  <Typography
                                    sx={{
                                      color: shortage
                                        ? '#f87171'
                                        : '#86efac',
                                      fontSize: 13,
                                      fontWeight: 950,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {fmt(day.closingBalance)}
                                  </Typography>
                                </Stack>

                                <Typography
                                  sx={{
                                    color: '#64748b',
                                    fontSize: 9,
                                    mt: 0.2,
                                  }}
                                >
                                  {[
                                    day.requirementCount > 0
                                      ? `${day.requirementCount} OF`
                                      : null,
                                    day.purchaseCount > 0
                                      ? `${day.purchaseCount} pedido${day.purchaseCount === 1 ? '' : 's'}`
                                      : null,
                                  ].filter(Boolean).join(' · ') || 'Sin movimientos'}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : (
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: 12,
                  py: 2,
                }}
              >
                No hay eventos con fecha utilizable.
              </Typography>
            )}
          </Paper>

          <Box
            sx={{
              display: 'grid',
              gridTemplateRows: {
                xs: 'repeat(2, minmax(260px, 1fr))',
                xl: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1.5,
              minHeight: 0,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, pt: 1.5, pb: 0.75, flexShrink: 0 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <FactoryRounded sx={{ color: '#f87171' }} />
                  <Typography sx={{ fontWeight: 950 }}>
                    Necesidades
                  </Typography>
                </Stack>

                <Chip
                  size="small"
                  label={
                    selectedDay
                      ? `${fmtDate(selectedDay)} · ${visibleRequirements.length} OF`
                      : `${visibleRequirements.length} OF`
                  }
                  sx={{
                    height: 22,
                    fontSize: 9.5,
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <RequirementsTable
                events={visibleRequirements}
                emptyText="Sin necesidades para la selección."
              />
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2.5,
                overflow: 'hidden',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, pt: 1.5, pb: 0.75, flexShrink: 0 }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <LocalShippingRounded sx={{ color: '#38bdf8' }} />
                  <Typography sx={{ fontWeight: 950 }}>
                    Pedidos de compra
                  </Typography>
                </Stack>

                <Chip
                  size="small"
                  label={
                    selectedDay
                      ? `${fmtDate(selectedDay)} · ${visiblePurchases.length}`
                      : `${visiblePurchases.length} pedidos`
                  }
                  sx={{
                    height: 22,
                    fontSize: 9.5,
                    fontWeight: 850,
                  }}
                />
              </Stack>

              <PurchasesTable
                events={visiblePurchases}
                emptyText={selectedDay ? "Sin pedidos para la selección." : "Sin pedidos de compra pendientes"}
                emptyIsWarning={!selectedDay}
              />
            </Paper>
          </Box>
        </Box>
        </Collapse>
        </>
        ) : null}

        {tab === 'consumo' ? (
          detail ? (
            <ConsumptionTab detail={detail} unit={unitLabel ?? ''} />
          ) : detailQuery.isError ? (
            detailErrorBlock
          ) : (
            <DetailLoading height={420} />
          )
        ) : null}

        {tab === 'entradas' ? (
          <>
            {detailQuery.isError ? detailErrorBlock : null}
            <EntriesSection
              detail={detail}
              failed={detailQuery.isError}
              unit={unitLabel ?? ''}
              entriesSinceCount={material.entries_since_count_qty}
              baselineDate={material.baseline_count_date}
            />
            <Paper
              variant="outlined"
              sx={{ borderRadius: 2.5, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, pt: 1.5, pb: 0.75, flexShrink: 0 }}>
                <LocalShippingRounded sx={{ color: '#38bdf8' }} />
                <Typography sx={{ fontWeight: 950 }}>Pedidos de compra pendientes</Typography>
              </Stack>
              <PurchasesTable
                events={material.purchase_events}
                emptyText="Sin pedidos de compra pendientes"
                emptyIsWarning
                shortageDate={material.first_shortage_date}
              />
            </Paper>
          </>
        ) : null}

        {tab === 'of' ? (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2.5, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1.5, pt: 1.5, pb: 0.75, flexShrink: 0 }}>
              <FactoryRounded sx={{ color: '#f87171' }} />
              <Typography sx={{ fontWeight: 950 }}>Necesidades de OF</Typography>
            </Stack>
            <RequirementsTable events={material.requirement_events} emptyText="Sin necesidades de OF." />
          </Paper>
        ) : null}

        {tab === 'recuentos' ? (
          detail ? (
            <CountsTab detail={detail} stock={material.calculated_stock_qty} unit={unitLabel ?? ''} />
          ) : detailQuery.isError ? (
            detailErrorBlock
          ) : (
            <DetailLoading />
          )
        ) : null}

        {tab === 'datos' ? (
          <>
            {/* IND-020 */}
            <IndustrialCostStockExplanationBlock material={material} />
            {material.warnings.length > 0 ? (
              <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.5 }}>
                <Typography sx={{ fontWeight: 950, fontSize: 13.5, mb: 0.75 }}>Avisos del material</Typography>
                <Stack spacing={0.6}>
                  {material.warnings.map((warning) => (
                    <Box key={warning.code}>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 850,
                          color: warning.severity === 'critical' ? '#f87171' : '#fbbf24',
                        }}
                      >
                        {warning.title}
                      </Typography>
                      <Typography sx={{ fontSize: 11.5, color: '#cbd5e1' }}>{warning.message}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ) : null}
            {detail ? <HistoryNotes detail={detail} /> : null}
            {!material.warnings.length && !material.stock_explanation && !material.baseline_assumed_zero && !detail ? (
              <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>Sin avisos para este material.</Typography>
            ) : null}
          </>
        ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
